import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignSelfProps,
  BackgroundColorProps,
  BorderRadiusProps,
  PositionProps,
  SpaceProps,
  ZIndexProps,
} from 'styled-system';
import { alignSelf, backgroundColor, borderRadius, position, space, zIndex } from 'styled-system';

import LegalDisclaimer from 'common/components/LegalDisclaimer';
import { LEGAL_DISCLAIMER_BANNER_Z_INDEX } from 'common/constants/z-index';
import { useLocalization } from 'common/providers/LocaleProvider';
import {
  themeBottom,
  themeRight,
  type ThemeBottomProps,
  type ThemeRightProps,
} from 'common/theme/system-utilities';

const StyledLegalDisclaimer = styled(LegalDisclaimer)<
  SpaceProps &
    PositionProps &
    ZIndexProps &
    ThemeBottomProps &
    ThemeRightProps &
    AlignSelfProps &
    BackgroundColorProps &
    BorderRadiusProps
>`
  ${space};
  ${position};
  ${zIndex};
  ${themeBottom};
  ${themeRight};
  ${alignSelf};
  ${backgroundColor};
  ${borderRadius};
`;

const legalDisclaimerStyles: Omit<
  ComponentProps<typeof StyledLegalDisclaimer>,
  'label' | 'content' | 'color'
> = {
  position: 'absolute',
  zIndex: LEGAL_DISCLAIMER_BANNER_Z_INDEX,
  themeRight: ['sp_8', null, 'sp_64'],
  themeBottom: ['sp_8', null, 'sp_24'],
  p: 'sp_8',
  backgroundColor: 'sys.neutral.background.default',
  borderRadius: 'medium',
};

const BannerLegalDisclaimer = ({
  label,
  content,
  color,
}: {
  label: React.ReactNode;
  content: string;
  color?: string;
}) => {
  const { t } = useLocalization();

  return (
    <StyledLegalDisclaimer
      label={label}
      content={content}
      color={color || 'sys.neutral.text.strong'}
      ariaLabel={t('sponsored_more_aria')}
      {...legalDisclaimerStyles}
    />
  );
};

export default BannerLegalDisclaimer;
